import ModalComponent from "../../../Components/ModalComponent";
import { Button, Form, Input } from "antd";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "../../../apis/store";
import { showNotitfication } from "../../../utils/Utils";
import { useTranslation } from "react-i18next";
import {
  toGetProfil,
  ToTransferBalance,
} from "../../../apis/actions/auth.actions";
import { useState } from "react";
import { RiExchangeLine } from "react-icons/ri";

interface GiftModalProps {
  onClose?: () => void;
  open?: boolean;
}

const GiftModal = ({ open, onClose }: GiftModalProps) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const dispatch = useDispatch<AppDispatch>();
  const { t, i18n } = useTranslation();
  const { profil } = useSelector((state: RootState) => state?.login);

  const validateSaudiPhone = (_: unknown, value: string) => {
    const saudiPhoneRegex = /^5[0-9]{8}$/;
    if (!value) {
      return Promise.reject(new Error(t("otp.warning.phone")));
    }
    if (!saudiPhoneRegex.test(value)) {
      return Promise.reject(new Error(t("register.invalidSaudiPhone")));
    }
    return Promise.resolve();
  };

  const handleApplyNewReward = async () => {
    try {
      await form.validateFields();
      const values = form.getFieldsValue();
      const mobile = values.phone;
      setLoading(true);

      const success = (await dispatch(
        ToTransferBalance({
          mobile: mobile,
          amount: values.amount,
        })
      )) as { payload?: { data?: { result?: { code?: number; message?: string } } } };

      const verifyOtp = success?.payload?.data?.result;
      const code = verifyOtp?.code;

      if (code === 200) {
        showNotitfication(
          i18n.language === "ar"
            ? "تم إهداء الرصيد بنجاح!"
            : "Balance transferred successfully!",
          "success"
        );
        dispatch(toGetProfil(i18n.language));

        handleClose();
      } else if (code === 404) {
        showNotitfication(
          i18n.language === "ar"
            ? "رقم الجوال غير صحيح أو غير مسجل."
            : "The mobile number is incorrect or not registered.",
          "error",
          true
        );
      } else if (code === 400) {
        showNotitfication(
          i18n.language === "ar"
            ? "الرصيد المتاح غير كافٍ."
            : "Insufficient available balance.",
          "error",
          true
        );
      } else {
        const errorMessage =
          verifyOtp?.message ||
          (i18n.language === "ar"
            ? "حدث خطأ غير متوقع!"
            : "An unexpected error occurred!");
        showNotitfication(errorMessage, "error", true);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    form.resetFields();
    onClose?.();
  };

  const isRtl = i18n.language === "ar";
  const balanceHint =
    profil?.data?.wallet_balance != null
      ? Number(profil.data.wallet_balance).toFixed(2)
      : "—";

  return (
    <ModalComponent
      width={480}
      title={
        <div className={`ac-wallet-modal__head${isRtl ? " ac-wallet-modal__head--rtl" : ""}`}>
          <div className="ac-wallet-modal__icon" aria-hidden>
            <RiExchangeLine />
          </div>
          <div className="ac-wallet-modal__head-text">
            <h2 className="ac-wallet-modal__title ac--font-expo">{t("profile.gift")}</h2>
            <p className="ac-wallet-modal__lead">{t("profile.modal_transfer_lead")}</p>
          </div>
        </div>
      }
      open={open}
      onClose={handleClose}
      closeOnOutsideClick={true}
      centered
      destroyOnClose
      className="ac-modal-wallet"
      canvasBackdrop
    >
      <div
        className={`ac-wallet-modal__body${isRtl ? " ac-wallet-modal__body--rtl" : ""}`}
        dir={isRtl ? "rtl" : "ltr"}
      >
        <div className="ac-wallet-modal__balance-pill">
          <span className="ac-wallet-modal__balance-label">{t("profile.balance")}</span>
          <span className="ac-wallet-modal__balance-value icon-saudi_riyal icon-saudi_riyal">
            {balanceHint}
          </span>
        </div>

        <Form form={form} layout="vertical" onFinish={handleApplyNewReward} className="ac-wallet-modal__form">
          <Form.Item
            label={<span className="ac-wallet-modal__label">{t("profile.account.phone")}</span>}
            name="phone"
            rules={[{ validator: validateSaudiPhone }]}
          >
            <Input
              addonBefore={
                <span className="ac-wallet-modal__prefix-code">966</span>
              }
              placeholder="05XXXXXXX"
              type="text"
              className="ac--input-login ac-wallet-modal__input-control"
              size="large"
              maxLength={9}
              autoComplete="tel-national"
              onChange={(e) => {
                const value = e.target.value.replace(/\D/g, "");
                form.setFieldsValue({ phone: value });
              }}
            />
          </Form.Item>

          <Form.Item
            label={<span className="ac-wallet-modal__label">{t("result.amount")}</span>}
            name="amount"
            rules={[
              { required: true, message: t("otp.warning.amount") },
              {
                validator: (_, value) => {
                  if (
                    value !== undefined &&
                    value !== null &&
                    String(value).length > 0 &&
                    (String(value).includes("-") || String(value).includes(","))
                  ) {
                    return Promise.reject(
                      i18n.language === "ar"
                        ? "الرجاء إدخال ارقام موجبة"
                        : "Please enter positive number!"
                    );
                  }
                  return Promise.resolve();
                },
              },
            ]}
          >
            <Input
              placeholder={t("result.amount")}
              type="number"
              className="ac--input-login ac-wallet-modal__input-control ac-wallet-modal__input--amount"
              size="large"
              min={0}
              onKeyDown={(e) => {
                if (["-", ".", ",", "e", "E"].includes(e.key)) {
                  e.preventDefault();
                }
              }}
              {...(i18n.language === "en"
                ? {
                    addonBefore: (
                      <span
                        className="ac-wallet-modal__prefix-currency icon-saudi_riyal ac-wallet-modal__riyal"
                        aria-hidden
                      />
                    ),
                  }
                : {
                    addonAfter: (
                      <span
                        className="ac-wallet-modal__prefix-currency icon-saudi_riyal ac-wallet-modal__riyal"
                        aria-hidden
                      />
                    ),
                  })}
            />
          </Form.Item>

          <Form.Item className="ac-wallet-modal__submit-wrap">
            <Button
              htmlType="submit"
              type="primary"
              block
              size="large"
              className="ac-wallet-modal__submit ac--btn-primary-jflatbold"
              disabled={loading}
              loading={loading}
            >
              {loading ? t("btn.loading") : t("btn.send_otp")}
            </Button>
          </Form.Item>
        </Form>
      </div>
    </ModalComponent>
  );
};

export default GiftModal;
